import type { WsEvent, WsStatus } from "../api/ws";

/**
 * Circuit-breaker chain status, driven by the trading WebSocket
 * stream that landed in Session 41.
 *
 * The breaker codes below are authoritative — each backend
 * breaker class in {@code com.dax.bot.algo.risk.breakers} defines
 * the static {@code CODE} constant that ends up on the wire. Kept in
 * sync with the backend by the parity test in GatesRail.test.tsx.
 * V4 Lot 5(unify-modes): the position/cycle age breakers were deleted, and the
 * whole breaker chain is now mode-agnostic — DEMO vs LIVE is a deployment fact
 * (one running pod is one mode), never a per-breaker scope in the UI.
 *
 * <h2>Status model</h2>
 * <ul>
 *   <li>{@code unknown} — no information yet (WS not open, or open
 *       with no triggered events seen). This is also the explicit
 *       safe default: we never claim "ok" without evidence.</li>
 *   <li>{@code tripped} — a {@code circuit_breaker.triggered} WS
 *       event has been received with the matching {@code code}.
 *       The {@code detail} field carries the matching breaker's
 *       message.</li>
 *   <li>{@code wsUnavailable} (separately reported as
 *       {@link BreakerStateResult#wsAvailable}) — distinct from
 *       "unknown" so the panel can show an explicit ERROR banner
 *       per the B5b partial-un-stubbing rule.</li>
 * </ul>
 *
 * <p>Reset: every {@code cycle.started} event clears the tripped
 * set so a new cycle starts with a clean board.
 */

export type BreakerStatus = "ok" | "tripped" | "unknown";

export interface BreakerEntry {
  code: string;
  /** Short human label. */
  label: string;
  status: BreakerStatus;
  /** Free-form detail (only populated when tripped). */
  detail?: string;
}

export interface BreakerStateResult {
  entries: BreakerEntry[];
  /** True while the WS is open. False signals the panel to show ERROR. */
  wsAvailable: boolean;
}

// PROTECTED_LADDER_V6 chain — 12 breakers, in @Order. The time-based
// CIRCUIT_END_OF_SESSION_FLATTEN + CIRCUIT_WEEKEND_HOLD were removed at the V6
// cutover and replaced by the marketStatus-driven pair below (§6): CIRCUIT_MARKET_CLOSED
// (hard close → flatten) + CIRCUIT_MARKET_RESTRICTED (soft settlement/auction → hold).
// (Historical decision_events still carrying the old codes render via closeLabels.)
const BREAKER_CATALOG: ReadonlyArray<Omit<BreakerEntry, "status">> = [
  { code: "CIRCUIT_MANUAL_EMERGENCY_STOP", label: "Manual emergency stop" },
  { code: "CIRCUIT_CYCLE_RISK_BUDGET", label: "Cycle cumulative-loss cap" },
  { code: "CIRCUIT_WORST_CASE_RISK", label: "Worst-case loss vs budget (§7)" },
  { code: "CIRCUIT_PYRAMIDING", label: "Pyramiding (max risky / direction)" },
  { code: "CIRCUIT_MAX_TOTAL_POSITIONS", label: "Max total open positions (book cap)" },
  { code: "CIRCUIT_CONSECUTIVE_STOP", label: "Consecutive stops" },
  { code: "CIRCUIT_STREAM_HEALTH", label: "Stream health (spread / quote age)" },
  { code: "CIRCUIT_DAILY_LOSS_LIMIT", label: "Daily loss limit" },
  { code: "CIRCUIT_WEEKLY_LOSS_LIMIT", label: "Weekly loss limit" },
  { code: "CIRCUIT_MARKET_CLOSED", label: "Market closed — flatten + block (weekend / holiday, §6)" },
  { code: "CIRCUIT_MARKET_RESTRICTED", label: "Market restricted — hold, no new entries (settlement / auction, §6)" },
  { code: "CIRCUIT_CAPITAL_INJECTION", label: "Capital injection guardrail" },
];

export function useBreakerState(
  events: WsEvent[],
  wsStatus: WsStatus,
): BreakerStateResult {
  // Walk the event buffer once: clear on cycle.started, remember
  // the latest detail per tripped code. The buffer is a rolling
  // window; the truncation is OK for breaker UX because what
  // matters is the most recent state, not history.
  const tripped = new Map<string, string | undefined>();
  for (const e of events) {
    if (e.type === "cycle.started") {
      tripped.clear();
      continue;
    }
    if (e.type === "circuit_breaker.triggered") {
      const d = e.data;
      if (d && typeof d === "object") {
        const obj = d as Record<string, unknown>;
        const code = typeof obj.code === "string" ? obj.code : null;
        if (code) {
          const detail = typeof obj.detail === "string" ? obj.detail : undefined;
          tripped.set(code, detail);
        }
      }
    }
  }

  const entries = BREAKER_CATALOG.map((b) => {
    if (tripped.has(b.code)) {
      return { ...b, status: "tripped" as const, detail: tripped.get(b.code) };
    }
    return { ...b, status: "unknown" as const };
  });

  return { entries, wsAvailable: wsStatus === "open" };
}

export function listBreakerCodes(): string[] {
  return BREAKER_CATALOG.map((b) => b.code);
}
